import apiClient from "../clients/apiClient";
import { ApiResponse } from "../schemas/apiResponseSchema";

export interface ItemResponse {
    id?: number | null;
    item_id?: number | null;
    item_code?: string | null;
    item_name?: string | null;
    item_webname?: string | null;
    item_group1?: string | null;
    item_uom?: string | null;
    item_case?: number | null;
    item_selling?: number | null;
    item_onlselling?: number | null;
    item_cost?: number | null;
    item_avgcost?: number | null;
    item_gp?: number | null;
    item_fixedgp?: number | null;
    item_reordermin?: number | null;
    item_reordermax?: number | null;
    item_enablereorderalert?: boolean | null;
    item_shortdescription?: string | null;
    item_barcode?: string | null;
    item_qrcode?: string | null;
    item_positem?: boolean | null;
    item_dirgrn?: boolean | null;
    item_hoprice?: boolean | null;
    item_vendorplu?: string | null;
    item_active?: boolean | null;
    item_generagebatch?: boolean | null;
    item_allowstcode?: boolean | null;
    item_calreceipe?: boolean | null;
    item_rawitem?: boolean | null;
    item_activedis?: boolean | null;
    item_weighted?: boolean | null;
    is_returnable?: boolean | null;
    status?: number | null;
}

export interface ItemListFilters {
    search?: string;
    status?: string;
}

export const getAllItems = async (
    page: number = 1,
    limit: number = 100,
    filters: ItemListFilters = {},
): Promise<ItemResponse[]> => {
    const params = {
        page,
        limit,
        ...(filters.search?.trim() ? { search: filters.search.trim() } : {}),
        ...(filters.status ? { status: filters.status } : {}),
    };

    try {
        const response: ApiResponse<ItemResponse[] | null> = (
            await apiClient.get("/pos_item/all", {
                params,
                headers: {
                    'x-skip-auth-redirect': 'true',
                },
            })
        ).data;
        return response.data || [];
    } catch (error: any) {
        // Fallback for environments still exposing legacy item endpoint.
        if (error?.response?.status === 404 || error?.response?.status === 405) {
            try {
                const fallbackResponse: ApiResponse<ItemResponse[] | null> = (
                    await apiClient.get("/it_product/all", {
                        params,
                        headers: {
                            'x-skip-auth-redirect': 'true',
                        },
                    })
                ).data;
                return fallbackResponse.data || [];
            } catch (fallbackError: any) {
                if (fallbackError.response?.status === 401) {
                    throw new Error("Not authorized to load items. Please sign in with backend authentication.");
                }
                if (fallbackError.response?.data?.detail) {
                    const detail = fallbackError.response.data.detail;
                    const message = typeof detail === 'string' ? detail : JSON.stringify(detail);
                    throw new Error(message);
                }
                throw new Error("Failed to fetch items from fallback endpoint");
            }
        }

        if (error?.response?.status === 401) {
            throw new Error("Not authorized to load items. Please sign in with backend authentication.");
        }
        if (error.response?.data?.detail) {
            const detail = error.response.data.detail;
            const message = typeof detail === 'string' ? detail : JSON.stringify(detail);
            throw new Error(message);
        }
        throw new Error("Failed to fetch items");
    }
};

export const createItem = async (itemData: Omit<ItemResponse, "id">): Promise<ItemResponse> => {
    try {
        const response: ApiResponse<ItemResponse> = (
            await apiClient.post("/pos_item/create", itemData, {
                headers: {
                    'x-skip-auth-redirect': 'true',
                },
            })
        ).data;
        return response.data;
    } catch (error: any) {
        if (error.response?.data?.detail) {
            const detail = error.response.data.detail;
            const message = typeof detail === 'string' ? detail : JSON.stringify(detail);
            throw new Error(message);
        }
        throw new Error("Failed to create item");
    }
};

export const updateItem = async (itemCode: string, itemData: Omit<ItemResponse, "id">): Promise<ItemResponse> => {
    try {
        const response: ApiResponse<ItemResponse> = (
            await apiClient.put(`/pos_item/update/${encodeURIComponent(itemCode)}`, itemData, {
                headers: {
                    'x-skip-auth-redirect': 'true',
                },
            })
        ).data;
        return response.data;
    } catch (error: any) {
        if (error.response?.data?.detail) {
            const detail = error.response.data.detail;
            const message = typeof detail === 'string' ? detail : JSON.stringify(detail);
            throw new Error(message);
        }
        throw new Error("Failed to update item");
    }
};
